import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { adminApi } from '../../services/api';
import AgGridTable from '../../components/AgGridTable';
import SidebarPanel from '../../components/admin/SidebarPanel';
import FormManagementSidebar from '../../components/admin/FormManagementSidebar';

const FormsManagementPage = () => {
  const navigate = useNavigate();
  const gridRef = useRef(null);
  const { formId } = useParams();
  const [searchParams] = useSearchParams();
  const version = searchParams.get('version');

  const [forms, setForms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFormCreation, setShowFormCreation] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [showVersionsPanel, setShowVersionsPanel] = useState(false);
  const [selectedFormForVersions, setSelectedFormForVersions] = useState(null);

  // Custom Cell Renderer for Actions column
  const ActionsCellRenderer = (props) => {
    const { data } = props;
    if (!data) return null;

    return (
      <div className="btn-group btn-group-sm" role="group">
        <button
          className="btn btn-outline-primary"
          onClick={() => handleEditClick(data)}
          title={`Edit version ${data.version}`}
        >
          <i className="fas fa-edit me-1"></i>
          Edit
        </button>
        <Link
          to={`/admin/forms/preview/${data.form_id}?version=${data.version}`}
          className="btn btn-outline-secondary"
          title={`Preview version ${data.version}`}
        >
          <i className="fas fa-eye me-1"></i>
          Preview
        </Link>
        {data.is_active ? (
          <button
            className="btn btn-outline-danger"
            onClick={() => handleDeleteForm(data.form_id, data.version)}
            title="Deactivate this version"
          >
            <i className="fas fa-times me-1"></i>
            Deactivate
          </button>
        ) : (
          <button
            className="btn btn-outline-success"
            onClick={() => handleActivateForm(data.form_id, data.version)}
            title="Activate this version"
          >
            <i className="fas fa-check me-1"></i>
            Activate
          </button>
        )}
      </div>
    );
  };

  // ag-Grid Column Definitions
  const [columnDefs] = useState([
    {
      headerName: 'Form Name',
      field: 'name',
      rowGroup: true,
      hide: true,
      width: 200
    },
    { headerName: 'Form ID', field: 'form_id', width: 80 },
    {
      headerName: 'Version',
      field: 'version',
      width: 100,
      cellRenderer: (params) => {
        if (params.data) {
          return (
            <span className={`badge ${params.data.is_active ? 'bg-success' : 'bg-secondary'}`}>
              v{params.data.version}
            </span>
          );
        }
        return null;
      }
    },
    {
      headerName: 'Type',
      field: 'type',
      width: 150
    },
    {
      headerName: 'Description',
      field: 'description',
      width: 200
    },
    {
      headerName: 'Health Systems',
      field: 'health_systems',
      valueGetter: p => {
        const healthSystems = p.data?.health_systems;
        if (!healthSystems || !Array.isArray(healthSystems) || healthSystems.length === 0) {
          return 'N/A';
        }
        return healthSystems.join(', ');
      },
      width: 250
    },
    {
      headerName: 'Status',
      field: 'is_active',
      width: 100,
      cellRenderer: (params) => {
        if (params.data) {
          return (
            <span className={`badge ${params.data.is_active ? 'bg-success' : 'bg-secondary'}`}>
              {params.data.is_active ? 'Active' : 'Inactive'}
            </span>
          );
        }
        return null;
      }
    },
    {
      headerName: 'Created',
      field: 'created_at',
      valueGetter: p => {
        if (!p.data?.created_at) return 'N/A';
        return new Date(p.data.created_at).toLocaleString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          timeZoneName: 'short'
        });
      },
      width: 200
    },
    {
      headerName: 'Actions',
      field: 'actions',
      cellRenderer: ActionsCellRenderer,
      minWidth: 250,
      sortable: false,
      filter: false,
      pinned: 'right'
    }
  ]);

  useEffect(() => {
    fetchForms();
  }, []);

  // Handle direct URLs for create/edit forms
  useEffect(() => {
    // Handle /admin/forms/new
    if (formId === 'new' && !showFormCreation) {
      setEditingForm(null);
      setShowFormCreation(true);
      return;
    }
    // Handle /admin/forms/edit/123?version=2
    if (formId && formId !== 'new' && forms.length > 0 && !showFormCreation) {
      const formIdNum = parseInt(formId, 10);
      const versionNum = version ? parseInt(version, 10) : null;

      const formToEdit = forms.find(f =>
        f.form_id === formIdNum &&
        (!versionNum || f.version === versionNum)
      );

      if (formToEdit) {
        setEditingForm(formToEdit);
        setShowFormCreation(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId, version, forms]);

  const fetchForms = async () => {
    try {
      setIsLoading(true);
      const response = await adminApi.getAllForms();
      if (response.data.success) {
        const formsList = response.data.forms;
        // Sort by form name and version for better grouping display
        const sortedForms = formsList.sort((a, b) => {
          if (a.name !== b.name) {
            return a.name.localeCompare(b.name);
          }
          return b.version - a.version; // Latest version first within each form
        });
        setForms(sortedForms);
      } else {
        setError('Failed to load forms');
      }
    } catch (err) {
      console.error('Error fetching forms:', err);
      if (err.response && err.response.status === 403) {
        setError('Permission denied. This feature requires proper authentication and authorization. Please see the README for security considerations.');
      } else {
        setError(`Error: ${err.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };


  const handleDeleteForm = async (formId, version) => {
    if (!window.confirm(`Are you sure you want to delete form ID ${formId}, version ${version}? To prevent unintended consequences, this will only be a soft delete.`)) {
      return;
    }

    try {
      const response = await adminApi.deleteForm(formId, version);

      if (response.data.success) {
        // Update both state and ag-Grid using transaction API
        setForms(prevForms => {
          const updatedForms = prevForms.map(form => {
            if (form.form_id === formId && form.version === version) {
              return { ...form, is_active: false };
            }
            return form;
          });

          // Update ag-Grid using transaction API
          if (gridRef.current?.api) {
            const rowNode = gridRef.current.api.getRowNode(`${formId}-${version}`);
            if (rowNode && rowNode.data) {
              const updatedData = { ...rowNode.data, is_active: false };
              gridRef.current.api.applyTransaction({ update: [updatedData] });
            }
          }

          return updatedForms;
        });
      } else {
        setError(response.data.message || 'Failed to delete form');
      }
    } catch (err) {
      setError(`Error: ${err.message}`);
    }
  };

  const handleActivateForm = async (formId, version) => {
    if (!window.confirm(`Are you sure you want to activate form ID ${formId}, version ${version}?`)) {
      return;
    }

    try {
      const activationData = {
        is_active: true,
      };

      const response = await adminApi.updateForm(formId, activationData, version);

      if (response.data.success) {
        // Update both state and ag-Grid using transaction API
        setForms(prevForms => {
          const updatedForms = prevForms.map(form => {
            if (form.form_id === formId && form.version === version) {
              return { ...form, is_active: true };
            }
            return form;
          });

          // Update ag-Grid using transaction API
          if (gridRef.current?.api) {
            const rowNode = gridRef.current.api.getRowNode(`${formId}-${version}`);
            if (rowNode && rowNode.data) {
              const updatedData = { ...rowNode.data, is_active: true };
              gridRef.current.api.applyTransaction({ update: [updatedData] });
            }
          }

          return updatedForms;
        });
      } else {
        setError(response.data.message || 'Failed to activate form version');
      }
    } catch (err) {
      console.error('Error activating form:', err);
      setError(`Error: ${err.message}`);
    }
  };

  const handleCreateClick = () => {
    setEditingForm(null);
    setShowFormCreation(true);
    navigate('/admin/forms/new');
  };

  const handleEditClick = (form) => {
    setEditingForm(form);
    setShowFormCreation(true);
    navigate(`/admin/forms/edit/${form.form_id}?version=${form.version}`);
  };

  const handleCloseForm = () => {
    setShowFormCreation(false);
    setEditingForm(null);
    // Also close versions panel if open
    setShowVersionsPanel(false);
    setSelectedFormForVersions(null);
    // Navigate back to base forms page
    navigate('/admin/forms');
  };

  const handleViewVersionsClick = (form) => {
    setSelectedFormForVersions(form);
    setShowVersionsPanel(true);
    setShowFormCreation(false); // Close form creation panel if open
    setEditingForm(null); // Clear editing form
  };

  const handleCloseVersionsPanel = () => {
    setShowVersionsPanel(false);
    setSelectedFormForVersions(null);
  };

  return (
    <>
      {/* Page Title */}
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0 font-size-18">Forms Management</h4>
            <div className="page-title-right">
              <ol className="breadcrumb m-0">
                <li className="breadcrumb-item"><span>Admin</span></li>
                <li className="breadcrumb-item active">Forms</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-12">
          <div className="d-flex justify-content-end mb-3">
            <button className="btn btn-primary" onClick={handleCreateClick}>
              <i className="fas fa-plus-circle me-1"></i> Create New Form
            </button>
          </div>

          {error && <div className="alert alert-danger">{error}</div>}

          {isLoading ? (
            <div className="d-flex justify-content-center">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="card-body">
                {forms.length === 0 ? (
                  <div className="alert text-secondary text-center">
                    <p>No forms found. Create your first form to get started.</p>
                  </div>
                ) : (
                  <div>
                    <AgGridTable
                      ref={gridRef}
                      rowData={forms}
                      columnDefs={columnDefs}
                      // Add default properties for ag-Grid
                      defaultColDef={{
                        flex: 1,
                        minWidth: 100,
                        resizable: true,
                        sortable: true,
                        filter: true,
                      }}
                      // Enable row grouping
                      groupDisplayType="groupRows"
                      autoGroupColumnDef={{
                        headerName: "Form Name",
                        minWidth: 250,
                        cellRendererParams: {
                          suppressCount: true,
                        },
                      }}
                      // Provide a unique ID for each row
                      getRowId={(params) => `${params.data.form_id}-${params.data.version}`}
                      // Expand all groups by default
                      groupDefaultExpanded={-1}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Sidebar for Form Creation/Editing */}
      <FormManagementSidebar
        isOpen={showFormCreation}
        onClose={handleCloseForm}
        editingForm={editingForm}
        onSave={fetchForms}
      />

      {/* Sidebar for Form Versions */}
      <SidebarPanel
        isOpen={showVersionsPanel && selectedFormForVersions !== null}
        onClose={handleCloseVersionsPanel}
        title={selectedFormForVersions ? `Versions for ${selectedFormForVersions.name} (ID: ${selectedFormForVersions.form_id})` : ''}
      >
        {selectedFormForVersions && (
          selectedFormForVersions.versions.length === 0 ? (
            <div className="alert alert-info">No versions found for this form.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-striped table-sm">
                <thead>
                  <tr>
                    <th>Version</th>
                    <th>Description</th>
                    <th>Health Systems</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedFormForVersions.versions.map(version => (
                    <tr key={version.version} className={!version.is_active ? 'table-secondary' : ''}>
                      <td>{version.version}</td>
                      <td>{version.description || 'N/A'}</td>
                      <td>{version.health_systems?.join(', ') || 'N/A'}</td>
                      <td>{version.is_active ? 'Active' : 'Inactive'}</td>
                      <td>
                        {version.is_active ? (
                          <>
                            <Link to={`/admin/forms/edit/${selectedFormForVersions.form_id}?version=${version.version}`} className="btn btn-sm btn-outline-primary me-2">Edit</Link>
                            <Link to={`/admin/forms/preview/${selectedFormForVersions.form_id}?version=${version.version}`} className="btn btn-sm btn-outline-secondary me-2">Preview</Link>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDeleteForm(selectedFormForVersions.form_id, version.version)}
                            >
                              Deactivate
                            </button>
                          </>
                        ) : (
                          <button
                            className="btn btn-sm btn-outline-success"
                            onClick={() => handleActivateForm(selectedFormForVersions.form_id, version.version)}
                          >
                            Activate
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </SidebarPanel>
    </>
  );
};

export default FormsManagementPage;